<script>import { goto } from "$app/navigation";
import { Button } from "@infinitro/button";
import { Card } from "@infinitro/card";
import { GameCategory, GameSort, getGames } from "@infinitro/catalog-min";
import { cfgToThemeCdnPath } from "@infinitro/config";
import { Verbiage, t } from "@infinitro/content";
import {
  isMobile,
  loggedIn,
  providerBasePath,
  toGameSlug,
  toProviderSlug,
  toSiteUrl
} from "@infinitro/global";
import { Text } from "@infinitro/text";
import { onMount, tick } from "svelte";
export let closeModal = () => {
};
let position = 0;
let velocity = 0;
let spinning = false;
let spinSpeed = 0;
let containerElement;
let allGames = [];
let baseGames = [];
let displayGames = [];
let circleLength = 0;
const COPIES = 3;
let winnerIndex = null;
let baseWinnerIndex = null;
let mounted = false;
let cardWidth = 176;
let cardTotalWidth = 176 + 8;
const INITIAL_VELOCITY = 140;
const DECELERATION = 0.96;
onMount(async () => {
  const categories = [
    GameCategory.Popular,
    GameCategory.NewGames,
    GameCategory.LiveCasino,
    GameCategory.FeatureBuyIn,
    GameCategory.GameShows,
    GameCategory.Originals
  ];
  const promises = categories.map(
    (category) => getGames(1, 24, void 0, category, GameSort.Popular)
  );
  const results = await Promise.all(promises);
  allGames = results.flatMap(([games]) => games);
  baseGames = shuffleArray(allGames);
  circleLength = baseGames.length;
  if (circleLength === 0) return;
  displayGames = Array.from({ length: COPIES }, () => baseGames).flat();
  await tick();
  updateCardMetrics();
  centerInitialCard();
  window.addEventListener("resize", handleResize);
  setTimeout(() => {
    mounted = true;
  }, 100);
});
function handleResize() {
  if (!containerElement) return;
  updateCardMetrics();
  centerInitialCard();
}
function onDestroy() {
  window.removeEventListener("resize", handleResize);
}
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
function getCards() {
  if (!containerElement) return [];
  return Array.from(containerElement.querySelectorAll(".wheel-card"));
}
function updateCardMetrics() {
  const cards = getCards();
  if (!cards.length) return;
  const rect0 = cards[0].getBoundingClientRect();
  cardWidth = rect0.width;
  if (cards.length > 1) {
    const rect1 = cards[1].getBoundingClientRect();
    const center0 = rect0.left + rect0.width / 2;
    const center1 = rect1.left + rect1.width / 2;
    cardTotalWidth = Math.abs(center1 - center0);
  } else {
    cardTotalWidth = cardWidth + 8;
  }
}
function centerInitialCard() {
  if (!containerElement || displayGames.length === 0 || circleLength === 0) return;
  const middleCircleOffset = circleLength;
  const initialIndexInCircle = middleCircleOffset + Math.floor(circleLength / 2);
  position = -(initialIndexInCircle * cardTotalWidth + cardWidth / 2);
  containerElement.style.transform = `translateX(${position}px)`;
}
function determineWinner() {
  if (!containerElement || circleLength === 0 || displayGames.length === 0) return;
  const totalLen = displayGames.length;
  let rawIndex = Math.round((-position - cardWidth / 2) / cardTotalWidth);
  rawIndex = (rawIndex % totalLen + totalLen) % totalLen;
  winnerIndex = rawIndex;
  const mod = (rawIndex % circleLength + circleLength) % circleLength;
  baseWinnerIndex = mod;
  const targetPosition = -(rawIndex * cardTotalWidth + cardWidth / 2);
  smoothSnapTo(targetPosition);
}
function spin() {
  if (spinning || displayGames.length === 0) return;
  winnerIndex = null;
  baseWinnerIndex = null;
  velocity = INITIAL_VELOCITY;
  spinning = true;
  animateSpin();
}
function normalizeDuringSpin() {
  if (circleLength === 0) return;
  const totalLen = circleLength * COPIES;
  let centerIndex = (-position - cardWidth / 2) / cardTotalWidth;
  if (centerIndex < circleLength * 0.5) {
    position -= circleLength * cardTotalWidth;
    centerIndex += circleLength;
  }
  if (centerIndex > circleLength * (COPIES - 0.5)) {
    position += circleLength * cardTotalWidth;
    centerIndex -= circleLength;
  }
  centerIndex = (centerIndex % totalLen + totalLen) % totalLen;
}
function animateSpin() {
  if (!spinning) return;
  velocity *= DECELERATION;
  position -= velocity;
  spinSpeed = velocity;
  normalizeDuringSpin();
  if (containerElement) {
    containerElement.style.transform = `translateX(${position}px)`;
  }
  if (velocity < 1) {
    spinning = false;
    spinSpeed = 0;
    determineWinner();
  } else {
    requestAnimationFrame(animateSpin);
  }
}
function smoothSnapTo(targetPosition) {
  const snapVelocity = (targetPosition - position) * 0.2;
  if (Math.abs(targetPosition - position) > 1) {
    position += snapVelocity;
    if (containerElement) {
      containerElement.style.transform = `translateX(${position}px)`;
    }
    requestAnimationFrame(() => smoothSnapTo(targetPosition));
  } else {
    position = targetPosition;
    if (containerElement) {
      containerElement.style.transform = `translateX(${position}px)`;
    }
  }
}
function goToGame(game) {
  const url = `${providerBasePath(game.prov)}${toProviderSlug(
    game.prov
  )}/${game.prov?.toLowerCase() === "sports" ? game.game : toGameSlug(game.game)}`;
  goto(toSiteUrl(url, $loggedIn));
  closeModal();
}
</script>

<div class="lucky-wheel-container">
	{#if baseWinnerIndex !== null && !spinning}
		{#if $isMobile}
			<img src={cfgToThemeCdnPath(`/lucky-dip/pattern-mobile.svg`)} class="pattern" />
		{:else}
			<img src={cfgToThemeCdnPath(`/lucky-dip/pattern.svg`)} class="pattern" />
		{/if}
	{/if}

	<div class="wheel-title">
		<Text type="title" titleSize="h4" titleWeight="bold" textAlign="center">
			{$t(Verbiage.take_your_shot_today)}
		</Text>
	</div>

	<div class="wheel-track-container">
		<img src={cfgToThemeCdnPath(`/lucky-dip/selector.svg`)} class="wheel-pointer" />

		<div class="wheel-viewport" class:spinning-effect={spinning}>
			<div
				class="wheel-track"
				class:loaded={mounted}
				class:fast-spinning={spinSpeed > 80}
				class:medium-spinning={spinSpeed <= 80 && spinSpeed > 40}
				bind:this={containerElement}
			>
				{#each displayGames as game, index (game.game + index)}
					<a
						class="wheel-card"
						class:winner={winnerIndex === index}
						href={toSiteUrl(
							`${providerBasePath(game.prov)}${toProviderSlug(game.prov)}/${
								game.prov?.toLowerCase() === 'sports' ? game.game : toGameSlug(game.game)
							}`,
							$loggedIn
						) || '#'}
						on:click={() => closeModal()}
					>
						<Card item={game} showOverlay={false} isInteractive={false} />
					</a>
				{/each}
			</div>
		</div>

		<img src={cfgToThemeCdnPath(`/lucky-dip/selector.svg`)} class="wheel-pointer-bottom" />
	</div>

	<div class="buttons-container">
		{#if baseWinnerIndex !== null && !spinning}
			<Button
				onClick={spinning
					? () => {}
					: () => baseWinnerIndex !== null && goToGame(baseGames[baseWinnerIndex])}
				text={$t(Verbiage.try_it_now)}
				disabled={spinning}
				minWidth
				styleCustom="opacity: {spinning ? 0 : 1};"
			/>
		{/if}
		{#if !spinning}
			<Button
				onClick={spinning ? () => {} : spin}
				variant={baseWinnerIndex === null ? 'primary' : 'tertiary'}
				text={baseWinnerIndex === null ? $t(Verbiage.try_your_luck) : $t(Verbiage.spin_again)}
				disabled={spinning || displayGames.length === 0}
				minWidth
				styleCustom="opacity: {spinning ? 0 : 1};"
			/>
		{:else}
			<div class="spinning-placeholder" />
		{/if}
	</div>
</div>

<style>
	.lucky-wheel-container {
		display: flex;
		height: 100%;
		width: 100%;
		flex-direction: column;
		align-items: center;
		justify-content: center;
}

	@media (min-width: 1024px) {
		.lucky-wheel-container {
				gap: 0px;
				padding-top: 2rem;
				padding-bottom: 2rem;
		}
	}

	.wheel-viewport {
		position: relative;
		margin-top: 1rem;
		margin-bottom: 1rem;
		width: 100%;
		overflow: hidden;
		height: 300px;
}

	.wheel-viewport.spinning-effect::after {
		content: '';
		pointer-events: none;
		position: absolute;
		inset: 0px;
		z-index: 30;
		background: linear-gradient(
			90deg,
			var(--color-secondary-700) 0%,
			transparent 20%,
			transparent 80%,
			var(--color-secondary-700) 100%
		);
		opacity: 0.6;
		animation: pulse-glow 0.8s ease-in-out infinite;
	}

	@keyframes pulse-glow {
		0%,
		100% {
			opacity: 0.4;
		}
		50% {
			opacity: 0.7;
		}
	}

	.wheel-viewport::before {
		content: '';
		position: absolute;
		inset: 0px;
		background: radial-gradient(
			circle,
			var(--color-secondary-300) 0%,
			var(--color-secondary-500) 100%
		);
		pointer-events: none;
		z-index: 0;
	}

	.wheel-pointer {
		position: absolute;
		top: 0px;
		left: 50%;
		z-index: 20;
		height: 3rem;
		width: 3rem;
		--tw-translate-x: -50%;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
}

	.wheel-pointer-bottom {
		position: absolute;
		bottom: 0px;
		left: 45%;
		z-index: 20;
		height: 3rem;
		width: 3rem;
		--tw-translate-x: -50%;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
		transform: rotate(180deg);
}

	@media (min-width: 1024px) {
		.wheel-pointer-bottom {
				left: 47.5%;
		}
	}

	.wheel-track-container {
		position: relative;
		z-index: 10;
		width: 100%;
}

	.wheel-track {
		position: absolute;
		display: flex;
		align-items: center;
		gap: 0.5rem;
		height: 100%;
		will-change: transform;
		padding: 2rem 50%;
		opacity: 0;
		transition: opacity 0.3s ease;
		z-index: 20;
}

	.wheel-track.fast-spinning .wheel-card {
		filter: blur(3px) brightness(1.05);
		opacity: 0.85;
		transition:
			filter 0.05s ease,
			opacity 0.05s ease;
	}

	.wheel-track.medium-spinning .wheel-card {
		filter: blur(1.5px);
		opacity: 0.95;
		transition:
			filter 0.1s ease,
			opacity 0.1s ease;
	}

	.wheel-track.loaded {
		opacity: 1;
	}

	.wheel-card {
		flex-shrink: 0;
		width: 176px;
		transition:
			transform 0.3s ease,
			opacity 0.3s ease,
			filter 0.3s ease;
}

	.wheel-card.winner {
		transform: rotate(-2deg);
		filter: drop-shadow(0 0 20px var(--color-secondary-300));
	}

	.wheel-card :global(img) {
		content-visibility: visible !important;
	}

	.buttons-container {
		z-index: 20;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 1rem;
}

	@media (max-width: 1023px) {
		.lucky-wheel-container {
				justify-content: space-between;
		}

		.wheel-title {
				display: flex;
				width: 100%;
				flex: 1 1 0%;
				align-items: center;
				justify-content: center;
		}

		.wheel-track-container {
				flex: none;
		}

		.buttons-container {
				display: flex;
				width: 100%;
				flex: 1 1 0%;
				align-items: center;
				justify-content: center;
		}

		.wheel-viewport {
				margin-top: 0px;
				margin-bottom: 0px;
		}
	}

	@media (max-width: 640px) {
		.wheel-viewport {
			height: 250px;
		}

		.wheel-card {
			width: 140px;
		}
	}

	.pattern {
		position: absolute;
		top: 0px;
		left: 50%;
		z-index: 0;
		height: auto;
		width: 100%;
		--tw-translate-x: -50%;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
}

	.spinning-placeholder {
		height: 40px;
		width: 100%;
}
</style>
